import { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import type { Poll } from '@yawi/types';
import type { CreatePollDTO } from '@yawi/domain';
import { useI18n } from '@/shared/i18n';
import { useSection } from '@/app/providers';
import { Button } from '@/presentation/components/atoms/Button';
import { Input } from '@/presentation/components/atoms/Input';
import { SectionMultiSelect } from '@/modules/sections/ui/components/SectionMultiSelect';

interface PollFormModalProps {
  poll?: Poll | null;
  isRecreating?: boolean;
  onClose: () => void;
  onSave: (data: CreatePollDTO, pollId?: string) => Promise<void>;
}

export const PollFormModal: React.FC<PollFormModalProps> = ({ poll, isRecreating = false, onClose, onSave }) => {
  const { t } = useI18n();
  const { hasSections } = useSection();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (poll) {
      setTitle(poll.title);
      setDescription(poll.description || '');
      setOptions(poll.options.map((o) => o.text));
      setAllowMultiple(poll.allowMultiple);
      setIsAnonymous(poll.isAnonymous);
      setStartsAt(poll.startsAt.split('T')[0]);
      setEndsAt(poll.endsAt.split('T')[0]);
      setSectionIds(poll.sectionIds ?? []);
    } else {
      // Defaults for new poll
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      setStartsAt(today);
      setEndsAt(nextWeek);
      setSectionIds([]);
    }
  }, [poll]);

  const addOption = () => {
    setOptions([...options, '']);
  };

  const removeOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validOptions = options.filter((o) => o.trim() !== '');
    if (validOptions.length < 2) {
      setError(t('polls.form.minOptions'));
      return;
    }

    setSaving(true);
    try {
      await onSave(
        {
          title,
          description: description || undefined,
          options: validOptions,
          allowMultiple,
          isAnonymous,
          startsAt,
          endsAt,
          sectionIds,
        },
        poll?.id
      );
      onClose();
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-800 text-lg">
            {isRecreating 
              ? t('polls.admin.createAgain')
              : poll 
                ? t('common.edit') 
                : t('polls.admin.create')}
          </h3>
          <button onClick={onClose}>
            <X size={20} className="text-slate-400 hover:text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <Input
            label={t('polls.form.title')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              {t('polls.form.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-100 resize-none"
            />
          </div>

          {hasSections && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Visibilidad
              </label>
              <SectionMultiSelect
                value={sectionIds}
                onChange={setSectionIds}
                resourceLabel="encuesta"
                idPrefix="poll"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('polls.form.options')} *
            </label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Opción ${index + 1}`}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-100"
                  />
                  {options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addOption}
              className="mt-2 flex items-center gap-1 text-sm text-violet-600 hover:text-violet-700 font-medium"
            >
              <Plus size={16} />
              {t('polls.form.addOption')}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('polls.form.startsAt')}
              </label>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('polls.form.endsAt')}
              </label>
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-100"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allowMultiple}
                onChange={(e) => setAllowMultiple(e.target.checked)}
                className="w-4 h-4 text-violet-600 border-slate-300 rounded focus:ring-violet-500"
              />
              <span className="text-sm text-slate-700">{t('polls.form.allowMultiple')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="w-4 h-4 text-violet-600 border-slate-300 rounded focus:ring-violet-500"
              />
              <span className="text-sm text-slate-700">{t('polls.form.isAnonymous')}</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" className="flex-1" disabled={saving} isLoading={saving}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
